import { signal } from '@angular/core';
import { debounce, FieldTree, min, required, SchemaPathTree } from '@angular/forms/signals';
import { GroupingDto } from '../../../../core/mceapi.client.generated';
import { some } from '../../../../shared/functions/functional';
import { buildSpacingSchema, createSpacing, defaultSpacing, Spacing } from '../spacing/spacing.model';

export interface Grouping {
  parallelCables: number;
  numberOfCircuits: number;
  circuitSpacing: Spacing;
}

export function defaultGrouping(): Grouping {
  return {
    parallelCables: 1,
    numberOfCircuits: 1,
    circuitSpacing: defaultSpacing()
  };
}

export function buildGroupingSchema(path: SchemaPathTree<Grouping>) {
  required(path.parallelCables, { message: `Parallel cables is required` });
  min(path.parallelCables, 1, { message: `Parallel cables must be positive` });
  debounce(path.parallelCables, 500);

  required(path.numberOfCircuits, { message: `Number of circuits is required` });
  min(path.numberOfCircuits, 1, { message: `Number of circuits must be positive` });
  debounce(path.numberOfCircuits, 500);

  buildSpacingSchema(path.circuitSpacing);
}

export function createGrouping(dto: GroupingDto) {
  if (dto.parallelCables === undefined || dto.numberOfCircuits === undefined || dto.circuitSpacing === undefined) {
    throw new Error('Grouping DTO is missing required values.');
  }

  return signal<Grouping>({
    parallelCables: dto.parallelCables,
    numberOfCircuits: dto.numberOfCircuits,
    circuitSpacing: createSpacing(some(dto.circuitSpacing))(),
  });
}

export function createGroupingDto(model: FieldTree<Grouping>) {
  return signal<GroupingDto>({
    parallelCables: model.parallelCables().value(),
    numberOfCircuits: model.numberOfCircuits().value(),
    circuitSpacing: {
      case: model.circuitSpacing.case().value(),
      edgeToEdge: model.circuitSpacing.edgeToEdge().value(),
    }
  });
}
